import type { ActionFlowEngine } from '@cogo/runtime-v2'
import type { ContentRef } from '@cogo/publisher-core'
import type { AppV2Props, HostServices } from '@cogo/react-renderer-v2'
import {
  createHostCapabilityManifest,
  hashHostCapabilityContent,
  type HostCapabilityDeclaration,
  type HostCapabilityManifestDocument,
  type HostDatasourceDeclaration,
  type HostRegistryHandlerDeclaration,
  type HostValueSchema,
} from '@cogo/schema/host-capability-manifest'

import type { StudioRuntimePlatformBundle } from './studio-runtime-network'
import {
  isStudioWiredRegistryHandler,
  type StudioHostCapabilityBinding,
  type StudioHostServices,
} from './use-studio-host-services'
import { showStudioRuntimeToast } from './studio-runtime-toast'

export interface StudioExecutableCapabilityBinding {
  capabilityId: `host.${string}`
  operation: string
  adapterRef: ContentRef
  sideEffectClass: 'none' | 'reversible' | 'external'
  implementation: (...args: never[]) => unknown
}

export interface StudioDirectAppV2EffectBundle {
  appV2Props: {
    onNavigate: NonNullable<AppV2Props['onNavigate']>
    onToast: NonNullable<AppV2Props['onToast']>
    onReady: NonNullable<AppV2Props['onReady']>
  }
  cropImageImplementation?: StudioCropImageImplementation
  capturedHostServices: HostServices
  capabilityBindings: readonly StudioExecutableCapabilityBinding[]
}

export interface StudioDirectAppV2EffectInput {
  onNavigate: NonNullable<AppV2Props['onNavigate']>
  onToast: NonNullable<AppV2Props['onToast']>
  onReady: NonNullable<AppV2Props['onReady']>
  hostServices: HostServices
  cropImageImplementation?: StudioCropImageImplementation
}

export type StudioCropImageImplementation =
  typeof import('@cogo/react-renderer-v2')['cropImageWeb']

export interface StudioAdmittedDatasourceRow {
  datasourceId: string
  contractRef: ContentRef
  fixtureRefs: readonly ContentRef[]
}

export interface StudioHostCapabilityManifestInput {
  hostId: string
  hostBuildRef: ContentRef
  secretsBoundaryRef: ContentRef
  contextSchemas: Readonly<
    Record<'props' | 'event' | 'env' | 'route', HostValueSchema>
  >
  datasources: readonly HostDatasourceDeclaration[]
  admittedDatasourceRows: readonly StudioAdmittedDatasourceRow[]
  authContractRefs: readonly ContentRef[]
  consentContractRefs: readonly ContentRef[]
  confirmationContractRefs: readonly ContentRef[]
  idempotencyContractRefs: readonly ContentRef[]
  runtimePlatformBundle: StudioRuntimePlatformBundle
  hostServicesBundle: StudioHostServices
  directAppV2EffectBundle: StudioDirectAppV2EffectBundle
}

const STUDIO_DIRECT_APP_V2_EFFECT_DESCRIPTOR = {
  id: 'cogo.studio.direct-app-v2-effects.v1',
  implementation:
    'apps/cogo-studio/src/shell/ide-v2/components/build-mode-canvas.tsx',
  operations: {
    navigation: ['push', 'replace'],
    presentation: ['loading', 'popup', 'toast'],
    media: ['crop-image'],
  },
} as const

export const STUDIO_DIRECT_APP_V2_EFFECT_REF = {
  ref: 'cogo://studio/host-adapter/direct-app-v2-effects/v1',
  contentHash: hashHostCapabilityContent(STUDIO_DIRECT_APP_V2_EFFECT_DESCRIPTOR),
} as const

const STUDIO_DIRECT_READY_HOST_SERVICES = new WeakMap<Function, HostServices>()

export function createStudioDirectAppV2EffectBundle(
  input: StudioDirectAppV2EffectInput,
): StudioDirectAppV2EffectBundle {
  const onReady: NonNullable<AppV2Props['onReady']> = (context) => {
    wireStudioDirectAppV2Effects(context.actionEngine, input.hostServices)
    input.onReady(context)
  }
  STUDIO_DIRECT_READY_HOST_SERVICES.set(onReady, input.hostServices)
  return {
    appV2Props: {
      onNavigate: input.onNavigate,
      onToast: input.onToast,
      onReady,
    },
    cropImageImplementation: input.cropImageImplementation,
    capturedHostServices: input.hostServices,
    capabilityBindings: [
      {
        capabilityId: 'host.navigation',
        operation: 'push',
        adapterRef: STUDIO_DIRECT_APP_V2_EFFECT_REF,
        sideEffectClass: 'reversible',
        implementation: input.onNavigate as (...args: never[]) => unknown,
      },
      {
        capabilityId: 'host.navigation',
        operation: 'replace',
        adapterRef: STUDIO_DIRECT_APP_V2_EFFECT_REF,
        sideEffectClass: 'reversible',
        implementation: input.onNavigate as (...args: never[]) => unknown,
      },
      {
        capabilityId: 'host.presentation',
        operation: 'toast',
        adapterRef: STUDIO_DIRECT_APP_V2_EFFECT_REF,
        sideEffectClass: 'reversible',
        implementation: input.onToast as (...args: never[]) => unknown,
      },
      {
        capabilityId: 'host.presentation',
        operation: 'popup',
        adapterRef: STUDIO_DIRECT_APP_V2_EFFECT_REF,
        sideEffectClass: 'reversible',
        implementation: onReady as (...args: never[]) => unknown,
      },
      {
        capabilityId: 'host.presentation',
        operation: 'loading',
        adapterRef: STUDIO_DIRECT_APP_V2_EFFECT_REF,
        sideEffectClass: 'reversible',
        implementation: onReady as (...args: never[]) => unknown,
      },
      ...(typeof input.cropImageImplementation === 'function'
        ? [
            {
              capabilityId: 'host.media' as const,
              operation: 'crop-image',
              adapterRef: STUDIO_DIRECT_APP_V2_EFFECT_REF,
              sideEffectClass: 'reversible' as const,
              implementation: input.cropImageImplementation as (...args: never[]) => unknown,
            },
          ]
        : []),
    ],
  }
}

/** Wire the two presentation hooks not represented by AppV2's direct props. */
export function wireStudioDirectAppV2Effects(
  actionEngine: ActionFlowEngine,
  hostServices: StudioHostServices['hostServices'],
): void {
  actionEngine.onShowPopup = (params) => {
    hostServices.modalStore.open('runtimePresentationPopup', params)
  }
  actionEngine.onShowLoading = (show, options) => {
    showStudioRuntimeToast({
      message: show ? (options?.message ?? 'Loading') : 'Loading complete',
      variant: 'info',
    })
  }
}

function refKey(value: ContentRef): string {
  return `${value.ref}\u0000${value.contentHash}`
}

function capabilityBindings(
  input: StudioHostCapabilityManifestInput,
): StudioExecutableCapabilityBinding[] {
  return [
    ...input.runtimePlatformBundle.capabilityBindings,
    ...input.hostServicesBundle.capabilityBindings,
    ...input.directAppV2EffectBundle.capabilityBindings,
  ]
}

function declarations(input: StudioHostCapabilityManifestInput): HostCapabilityDeclaration[] {
  const grouped = new Map<
    string,
    {
      capabilityId: `host.${string}`
      operations: string[]
      adapterRef: ContentRef
      sideEffectClass: HostCapabilityDeclaration['sideEffectClass']
    }
  >()
  for (const binding of capabilityBindings(input)) {
    const existing = grouped.get(binding.capabilityId)
    if (existing) {
      if (
        refKey(existing.adapterRef) !== refKey(binding.adapterRef) ||
        existing.sideEffectClass !== binding.sideEffectClass
      ) {
        throw new Error(
          `STUDIO_HOST_CAPABILITY_DECLARATION_CONFLICT:${binding.capabilityId}`,
        )
      }
      if (existing.operations.includes(binding.operation)) {
        throw new Error(
          `STUDIO_HOST_WIRING_COLLISION:${binding.capabilityId}\u0000${binding.operation}`,
        )
      }
      existing.operations.push(binding.operation)
    } else {
      grouped.set(binding.capabilityId, {
        capabilityId: binding.capabilityId,
        operations: [binding.operation],
        adapterRef: binding.adapterRef,
        sideEffectClass: binding.sideEffectClass,
      })
    }
  }
  return [...grouped.values()].map((row) => ({
    ...row,
    operations: row.operations.sort(),
  }))
}

interface ExpectedExecutableBinding {
  capabilityId: `host.${string}`
  operation: string
  implementation: unknown
}

function registryKey(
  row: Pick<
    HostRegistryHandlerDeclaration,
    'capabilityId' | 'selector' | 'handlerRef'
  >,
): string {
  return `${row.capabilityId}\u0000${row.selector}\u0000${refKey(row.handlerRef)}`
}

function effectKey(
  row: Pick<StudioExecutableCapabilityBinding, 'capabilityId' | 'operation'>,
): string {
  return `${row.capabilityId}\u0000${row.operation}`
}

function assertExecutableBindings(
  expectedRows: readonly ExpectedExecutableBinding[],
  declaredRows: readonly StudioExecutableCapabilityBinding[],
): void {
  const expected = new Map<string, ExpectedExecutableBinding>()
  for (const row of expectedRows) {
    const key = effectKey(row)
    if (expected.has(key)) throw new Error(`STUDIO_HOST_WIRING_COLLISION:${key}`)
    expected.set(key, row)
  }
  const declared = new Map<string, StudioExecutableCapabilityBinding>()
  for (const row of declaredRows) {
    const key = effectKey(row)
    if (declared.has(key)) throw new Error(`STUDIO_HOST_WIRING_COLLISION:${key}`)
    declared.set(key, row)
  }
  for (const [key, actual] of [...expected].sort(([left], [right]) => left.localeCompare(right))) {
    const declaration = declared.get(key)
    if (!declaration || typeof actual.implementation !== 'function') {
      throw new Error(`STUDIO_HOST_WIRING_UNDECLARED:${key}`)
    }
    if (
      typeof declaration.implementation !== 'function' ||
      declaration.implementation !== actual.implementation
    ) {
      throw new Error(`STUDIO_HOST_WIRING_UNDECLARED:${key}:implementation`)
    }
  }
  for (const key of [...declared.keys()].sort()) {
    if (!expected.has(key)) throw new Error(`STUDIO_HOST_CAPABILITY_UNWIRED:${key}`)
  }
}

function assertExactStudioWiring(input: StudioHostCapabilityManifestInput): void {
  const runtimeFetch = input.runtimePlatformBundle.platform.fetch
  assertExecutableBindings(
    [
      { capabilityId: 'host.fetch', operation: 'fetch', implementation: runtimeFetch },
      { capabilityId: 'host.fetch', operation: 'fetch-with-headers', implementation: runtimeFetch },
    ],
    input.runtimePlatformBundle.capabilityBindings,
  )

  const services = input.hostServicesBundle.hostServices
  const motionImplementation = (
    services as HostServices & {
      motionRegistry?: { run?: (...args: never[]) => unknown }
    }
  ).motionRegistry?.run
  assertExecutableBindings(
    [
      { capabilityId: 'host.callback', operation: 'invoke', implementation: services.callbackRegistry.invoke },
      { capabilityId: 'host.event-bus', operation: 'emit', implementation: services.eventBus.emit },
      { capabilityId: 'host.modal', operation: 'open', implementation: services.modalStore.open },
      ...(typeof motionImplementation === 'function'
        ? [{ capabilityId: 'host.motion' as const, operation: 'run', implementation: motionImplementation }]
        : []),
    ],
    input.hostServicesBundle.capabilityBindings,
  )

  const direct = input.directAppV2EffectBundle
  if (
    direct.capturedHostServices !== services ||
    STUDIO_DIRECT_READY_HOST_SERVICES.get(direct.appV2Props.onReady) !== services
  ) {
    throw new Error(
      'STUDIO_HOST_WIRING_UNDECLARED:host.presentation\u0000installer-host-services',
    )
  }
  assertExecutableBindings(
    [
      { capabilityId: 'host.navigation', operation: 'push', implementation: direct.appV2Props.onNavigate },
      { capabilityId: 'host.navigation', operation: 'replace', implementation: direct.appV2Props.onNavigate },
      { capabilityId: 'host.presentation', operation: 'toast', implementation: direct.appV2Props.onToast },
      { capabilityId: 'host.presentation', operation: 'popup', implementation: direct.appV2Props.onReady },
      { capabilityId: 'host.presentation', operation: 'loading', implementation: direct.appV2Props.onReady },
      ...(typeof direct.cropImageImplementation === 'function'
        ? [{ capabilityId: 'host.media' as const, operation: 'crop-image', implementation: direct.cropImageImplementation }]
        : []),
    ],
    direct.capabilityBindings,
  )

  const actualHandlerKeys = input.hostServicesBundle.wiredRegistryHandlers.map((row) => {
    if (!isStudioWiredRegistryHandler(row)) {
      throw new Error('STUDIO_HOST_WIRING_UNDECLARED:registry-handler:unverified')
    }
    return registryKey(row)
  })
  const declaredHandlerKeys = input.hostServicesBundle.registryHandlers.map(registryKey)
  if (new Set(actualHandlerKeys).size !== actualHandlerKeys.length) {
    throw new Error('STUDIO_HOST_WIRING_COLLISION:registry-handler')
  }
  if (new Set(declaredHandlerKeys).size !== declaredHandlerKeys.length) {
    throw new Error('HOST_CAPABILITY_MANIFEST_DUPLICATE_REGISTRY_HANDLER')
  }
  const actualHandlers = new Set(actualHandlerKeys)
  const declaredHandlers = new Set(declaredHandlerKeys)
  for (const key of [...declaredHandlers].sort()) {
    if (!actualHandlers.has(key)) {
      throw new Error(`STUDIO_HOST_CAPABILITY_UNWIRED:${key}`)
    }
  }
  for (const key of [...actualHandlers].sort()) {
    if (!declaredHandlers.has(key)) {
      throw new Error(`STUDIO_HOST_WIRING_UNDECLARED:${key}`)
    }
  }
}

function assertDatasourcesAdmitted(input: StudioHostCapabilityManifestInput): void {
  const admitted = new Set<string>()
  for (const row of input.admittedDatasourceRows) {
    for (const fixtureRef of row.fixtureRefs) {
      admitted.add(
        `${row.datasourceId}\u0000${refKey(row.contractRef)}\u0000${refKey(fixtureRef)}`,
      )
    }
  }
  for (const row of input.datasources) {
    if (!row.fixtureRef) continue
    const key = `${row.datasourceId}\u0000${refKey(row.contractRef)}\u0000${refKey(row.fixtureRef)}`
    if (!admitted.has(key)) throw new Error(`STUDIO_HOST_FIXTURE_NOT_ADMITTED:${row.datasourceId}`)
  }
}

export function materializeStudioHostCapabilityManifest(
  input: StudioHostCapabilityManifestInput,
): HostCapabilityManifestDocument {
  const manifest = createHostCapabilityManifest({
    schemaVersion: 'cogo.host-capability-manifest.v1',
    hostId: input.hostId,
    hostBuildRef: input.hostBuildRef,
    contextSchemas: input.contextSchemas,
    capabilities: declarations(input),
    registryHandlers: input.hostServicesBundle.registryHandlers,
    storeHandlers: [],
    datasources: input.datasources,
    authContractRefs: input.authContractRefs,
    consentContractRefs: input.consentContractRefs,
    confirmationContractRefs: input.confirmationContractRefs,
    idempotencyContractRefs: input.idempotencyContractRefs,
    secretsBoundaryRef: input.secretsBoundaryRef,
  })
  assertExactStudioWiring(input)
  assertDatasourcesAdmitted(input)
  return manifest
}
